/* v8 ignore next */

import { withInstrumentation } from "@amzn/asx-ui-telemetry-client";
import type {
    CreatePackagedInsightRequest,
    CreatePackagedInsightResponse,
    CreateCuratedInsightRequest,
    CreateCuratedInsightResponse,
    GetDropdownValuesRequest,
    GetDropdownValuesResponse,
    GetGeneratedInsightsRequest,
    GetGeneratedInsightsResponse,
    GetGeneratedInsightRequest,
    GetGeneratedInsightResponse,
    GetInputSourcesByAdvertiserIdRequest,
    GetInputSourcesByAdvertiserIdResponse,
    GetSfOpportunitiesRequest,
    GetSfOpportunitiesResponse,
    CreateInsightLinkageRequest,
    CreateInsightLinkageResponse,
    GetInsightFamiliesRequest,
    GetInsightFamiliesResponse,
    GetInsightFamilyRequest,
    GetInsightFamilyResponse,
    GetNonEndemicTargetSegmentTypesRequest,
    GetNonEndemicTargetSegmentTypesResponse,
    GetNonEndemicTargetSegmentsRequest,
    GetNonEndemicTargetSegmentsResponse,
    GetSfActivitiesRequest,
    GetSfActivitiesResponse,
    GetSfAccountsRequest,
    GetSfAccountsResponse,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import {
    CreatePackagedInsightCommand,
    CreateCuratedInsightCommand,
    D16GClientDeckBuilderLambdaClient,
    GetDropdownValuesCommand,
    GetGeneratedInsightsCommand,
    GetGeneratedInsightCommand,
    GetInputSourcesByAdvertiserIdCommand,
    GetSfOpportunitiesCommand,
    CreateInsightLinkageCommand,
    GetInsightFamiliesCommand,
    GetInsightFamilyCommand,
    GetNonEndemicTargetSegmentsCommand,
    GetNonEndemicTargetSegmentTypesCommand,
    GetSfActivitiesCommand,
    GetSfAccountsCommand,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import { getCredentialsFromCacheOrCognito } from "@amzn/midway-cognito-type-script-provider";

import { TELEMETRY_OPERATIONS } from "../../constants/telemetry";
import { authStore } from "../../store/auth";
import { FeatureFlagsStore } from "../../store/featureFlags";
import { APPENV } from "../../utils/app.environment";
import { asxTelemetryClient } from "../../utils/telemetry.utils";
import { getCustomerId } from "../adsApi/adsApi.config";

export class CdbApiClient {
    public client: D16GClientDeckBuilderLambdaClient;

    constructor(client: D16GClientDeckBuilderLambdaClient) {
        this.client = client;
    }

    getDropdownValues: (
        request: GetDropdownValuesRequest,
    ) => Promise<GetDropdownValuesResponse> = withInstrumentation(
        asxTelemetryClient,
        TELEMETRY_OPERATIONS.GET_DROPDOWN_VALUES,
        async (request) => {
            return this.client.send(new GetDropdownValuesCommand(request));
        },
    );
    getSfOpportunities: (
        request: GetSfOpportunitiesRequest,
    ) => Promise<GetSfOpportunitiesResponse> = withInstrumentation(
        asxTelemetryClient,
        TELEMETRY_OPERATIONS.GET_SF_OPPORTUNITIES,
        async (request) => {
            return this.client.send(new GetSfOpportunitiesCommand(request));
        },
    );
    getSfActivities: (
        request: GetSfActivitiesRequest,
    ) => Promise<GetSfActivitiesResponse> = withInstrumentation(
        asxTelemetryClient,
        TELEMETRY_OPERATIONS.GET_SF_ACTIVITIES,
        async (request) => {
            return this.client.send(new GetSfActivitiesCommand(request));
        },
    );
    getSfAccounts: (
        request: GetSfAccountsRequest,
    ) => Promise<GetSfAccountsResponse> = withInstrumentation(
        asxTelemetryClient,
        TELEMETRY_OPERATIONS.GET_SF_ACCOUNTS,
        async (request) => {
            const customerId = getCustomerId();
            const requestWithHeader: GetSfAccountsRequest = {
                ...request,
                customerId,
            };
            return this.client.send(
                new GetSfAccountsCommand(requestWithHeader),
            );
        },
    );
    getInputSourcesByAdvertiserId: (
        request: GetInputSourcesByAdvertiserIdRequest,
    ) => Promise<GetInputSourcesByAdvertiserIdResponse> = withInstrumentation(
        asxTelemetryClient,
        TELEMETRY_OPERATIONS.GET_INPUT_SOURCES_BY_ADVERTISER_ID,
        async (request) => {
            return this.client.send(
                new GetInputSourcesByAdvertiserIdCommand(request),
            );
        },
    );
    getGeneratedInsights: (
        request: GetGeneratedInsightsRequest,
    ) => Promise<GetGeneratedInsightsResponse> = withInstrumentation(
        asxTelemetryClient,
        TELEMETRY_OPERATIONS.GET_GENERATED_INSIGHTS,
        async (request) => {
            if (FeatureFlagsStore.getState().uatPackagedInsights) {
                request.uat = true;
            }
            return this.client.send(new GetGeneratedInsightsCommand(request));
        },
    );
    getGeneratedInsight: (
        request: GetGeneratedInsightRequest,
    ) => Promise<GetGeneratedInsightResponse> = withInstrumentation(
        asxTelemetryClient,
        TELEMETRY_OPERATIONS.GET_GENERATED_INSIGHT,
        async (request) => {
            if (FeatureFlagsStore.getState().uatPackagedInsights) {
                request.uat = true;
            }
            return this.client.send(new GetGeneratedInsightCommand(request));
        },
    );
    createPackagedInsight: (
        request: CreatePackagedInsightRequest,
    ) => Promise<CreatePackagedInsightResponse> = withInstrumentation(
        asxTelemetryClient,
        TELEMETRY_OPERATIONS.CREATE_PACKAGED_INSIGHTS,
        async (request) => {
            if (FeatureFlagsStore.getState().uatPackagedInsights) {
                request.uat = true;
            }
            return this.client.send(new CreatePackagedInsightCommand(request));
        },
    );
    createCuratedInsight: (
        request: CreateCuratedInsightRequest,
    ) => Promise<CreateCuratedInsightResponse> = withInstrumentation(
        asxTelemetryClient,
        TELEMETRY_OPERATIONS.CREATE_CURATED_INSIGHT,
        async (request) => {
            return this.client.send(new CreateCuratedInsightCommand(request));
        },
    );
    getInsightFamily: (
        request: GetInsightFamilyRequest,
    ) => Promise<GetInsightFamilyResponse> = withInstrumentation(
        asxTelemetryClient,
        TELEMETRY_OPERATIONS.GET_INSIGHT_FAMILY,
        async (request) => {
            return this.client.send(new GetInsightFamilyCommand(request));
        },
    );
    getInsightFamilies: (
        request: GetInsightFamiliesRequest,
    ) => Promise<GetInsightFamiliesResponse> = withInstrumentation(
        asxTelemetryClient,
        TELEMETRY_OPERATIONS.GET_INSIGHT_FAMILIES,
        async (request) => {
            return this.client.send(new GetInsightFamiliesCommand(request));
        },
    );

    createInsightLinkage: (
        request: CreateInsightLinkageRequest,
    ) => Promise<CreateInsightLinkageResponse> = withInstrumentation(
        asxTelemetryClient,
        TELEMETRY_OPERATIONS.CREATE_INSIGHT_LINKAGE,
        async (request) => {
            return this.client.send(new CreateInsightLinkageCommand(request));
        },
    );
    getNonEndemicTargetSegments: (
        request: GetNonEndemicTargetSegmentsRequest,
    ) => Promise<GetNonEndemicTargetSegmentsResponse> = withInstrumentation(
        asxTelemetryClient,
        TELEMETRY_OPERATIONS.GET_NON_ENDEMIC_TARGET_SEGMENTS,
        async (request) => {
            return this.client.send(
                new GetNonEndemicTargetSegmentsCommand(request),
            );
        },
    );
    getNonEndemicTargetSegmentTypes: (
        request: GetNonEndemicTargetSegmentTypesRequest,
    ) => Promise<GetNonEndemicTargetSegmentTypesResponse> = withInstrumentation(
        asxTelemetryClient,
        TELEMETRY_OPERATIONS.GET_NON_ENDEMIC_TARGET_SEGEMENT_TYPES,
        async (request) => {
            return this.client.send(
                new GetNonEndemicTargetSegmentTypesCommand(request),
            );
        },
    );
}

export async function getCdbApiClient(): Promise<CdbApiClient> {
    try {
        const {
            settings: { cognitoPoolId, region, apiEndpoint },
        } = authStore.getState();

        if (!apiEndpoint) {
            throw new Error("CDB endpoint not yet defined");
        }

        const { accessKeyId, secretAccessKey, sessionToken } =
            await getCredentialsFromCacheOrCognito({
                region,
                identityPoolId: cognitoPoolId,
                clientId: window.location.host,
                redirectUri: window.location.href,
            });

        const client = new D16GClientDeckBuilderLambdaClient({
            endpoint: `${
                APPENV.mock_server ? "http" : "https"
            }://${apiEndpoint}`,
            region,
            credentials: { accessKeyId, secretAccessKey, sessionToken },
        });

        return new CdbApiClient(client);
    } catch (e: any) {
        throw new Error(e.message);
    }
}
